import type { CSSProperties } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { colors, fontSize, fontWeight, radius } from "../lib/tokens";
import { usePlatformAuth } from "../hooks/usePlatformAuth";
import { Wordmark } from "./Wordmark";
import {
  HomeIcon, BuildingIcon, CardIcon, ChartIcon,
  ShieldIcon, MegaphoneIcon, UsersIcon, CogIcon, LogOutIcon,
} from "./icons";

const sidebar: CSSProperties = {
  background: colors.ink,
  color: colors.paper,
  display: "flex",
  flexDirection: "column",
  padding: "20px 0",
  position: "sticky",
  top: 0,
  height: "100vh",
};

const brandRow: CSSProperties = {
  padding: "4px 24px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
};

const navWrap: CSSProperties = { flex: 1, padding: 12, overflowY: "auto" };

const sectionLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: fontWeight.semibold,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "rgba(255,255,255,0.35)",
  padding: "0 12px 8px",
};

const NAV_SECTIONS = [
  {
    label: "Operação",
    items: [
      { to: "/", label: "Visão geral", icon: HomeIcon, end: true },
      { to: "/tenants", label: "Tenants", icon: BuildingIcon },
      { to: "/billing", label: "Billing", icon: CardIcon },
      { to: "/metrics", label: "Métricas", icon: ChartIcon },
    ],
  },
  {
    label: "Confiança",
    items: [
      { to: "/audit", label: "Audit log", icon: ShieldIcon },
      { to: "/broadcast", label: "Broadcast", icon: MegaphoneIcon },
    ],
  },
  {
    label: "Klubora",
    items: [
      { to: "/team", label: "Equipe", icon: UsersIcon },
      { to: "/settings", label: "Configurações", icon: CogIcon },
    ],
  },
];

interface SidebarProps {
  /** Called after the user clicks any nav item — used by mobile drawer to auto-close. */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const { session, role, signOut } = usePlatformAuth();
  const navigate = useNavigate();

  const initials = (session?.user?.email ?? "")
    .split("@")[0]
    .split(/[.\-_]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  return (
    <aside style={sidebar}>
      <div style={brandRow}>
        <Wordmark variant="paper" height={22} />
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", paddingLeft: 31 }}>
          platform admin
        </div>
      </div>

      <nav style={navWrap}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} style={{ marginBottom: 20 }}>
            <div style={sectionLabel}>{section.label}</div>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    borderRadius: radius.sm,
                    color: isActive ? colors.paper : "rgba(255,255,255,0.78)",
                    fontSize: fontSize.md,
                    fontWeight: fontWeight.medium,
                    textDecoration: "none",
                    background: isActive ? "rgba(255,255,255,0.06)" : "transparent",
                    boxShadow: isActive ? `inset 2px 0 0 ${colors.indigo}` : undefined,
                    cursor: "pointer",
                    transition: "all 160ms cubic-bezier(0.32, 0.72, 0, 1)",
                  })}
                >
                  <Icon /> {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px 16px 4px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: radius.pill, background: colors.indigo, color: colors.paper, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: fontWeight.medium, fontSize: fontSize.sm, flexShrink: 0 }}>
          {initials || "?"}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.paper, lineHeight: 1.2 }}>
            {session?.user?.email?.split("@")[0] ?? "—"}
          </div>
          <div style={{ fontSize: fontSize.xs, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>
            {role ?? "—"}
          </div>
        </div>
        <button
          onClick={async () => { await signOut(); navigate("/login"); }}
          aria-label="Sair"
          style={{ background: "transparent", border: 0, color: "rgba(255,255,255,0.6)", cursor: "pointer", padding: 6, borderRadius: radius.sm, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <LogOutIcon width={14} height={14} />
        </button>
      </div>
    </aside>
  );
}
